import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { IChecklistSchedule } from '../../../checklist-v2';
import { FiChecklist2ScheduleFactoryService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-factory.service';
import { FiChecklist2ScheduleIntervalService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { AppTextService } from '@fi-shared/app-text';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { ObjectTypes } from '@fi-shared/types';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { Subject } from 'rxjs';

interface IChecklist2ScheduleAddDialogData {
    checklistSchedule: IChecklistSchedule;
    auditMode: boolean;
}

@Component({
    selector: 'fi-checklist2-schedule-add-dialog',
    templateUrl: './add-dialog.component.html',
    styleUrls: ['./add-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-schedule-add-dialog' },
    standalone: false,
})
export class FiChecklist2ScheduleAddDialogComponent
    extends FiDialogsContentBase<IChecklist2ScheduleAddDialogData, IChecklistSchedule>
    implements OnInit, OnDestroy
{
    @ViewChild(FiValidatingFormDirective) scheduleForm: FiValidatingFormDirective;

    private fiChecklist2ScheduleFactoryService: FiChecklist2ScheduleFactoryService;
    private fiChecklist2ScheduleIntervalService: FiChecklist2ScheduleIntervalService;
    private userDataService: FiUserDataService;
    private appTextService: AppTextService;
    private fiDialogsService: FiDialogsService;
    private fiChecklistHelperService: FiChecklistHelperService;

    selectedLanguageId: number;
    fiValuesComparison: FiValuesComparison<IChecklistSchedule>;

    get checklistSchedule(): IChecklistSchedule {
        return this.data.checklistSchedule;
    }

    get auditMode(): boolean {
        return this.data.auditMode;
    }

    ngOnInit() {
        this.setServices();
        this.selectedLanguageId = this.userDataService.userData.PreferredContentLanguage;
        this.fiValuesComparison = new FiValuesComparison(() => this.checklistSchedule);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    checklistScheduleChanged() {
        this.scheduleForm.updateValidity();
    }

    checklistScheduleTypeChanged() {
        this.checklistSchedule.TimeIntervalType = this.fiChecklist2ScheduleFactoryService.getIntervalTypeByScheduleType(
            this.checklistSchedule.ScheduleType,
        );
        this.checklistSchedule.TimeIntervals = this.fiChecklist2ScheduleIntervalService.getDefaultTimeIntervals(
            this.checklistSchedule.TimeIntervalType,
        );
        this.checklistScheduleChanged();
    }

    getResult(): IChecklistSchedule {
        return this.checklistSchedule;
    }

    getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(
            !this.fiValuesComparison.isChanged(),
            this.fiChecklistHelperService.getChecklistDiscardChangesDialogData(
                this.checklistSchedule.Id,
                ObjectTypes.ChecklistSchedule,
            ),
        );
    }

    isYesButtonDisabled(): boolean {
        return !!this.scheduleForm.errors || this.scheduleForm.pending;
    }

    private setServices() {
        this.fiChecklist2ScheduleFactoryService = this.injector.get(FiChecklist2ScheduleFactoryService);
        this.fiChecklist2ScheduleIntervalService = this.injector.get(FiChecklist2ScheduleIntervalService);
        this.userDataService = this.injector.get(FiUserDataService);
        this.appTextService = this.injector.get(AppTextService);
        this.fiDialogsService = this.injector.get(FiDialogsService);
        this.fiChecklistHelperService = this.injector.get(FiChecklistHelperService);
    }

    getNewScheduleHeader() {
        return this.appTextService.get(!this.auditMode ? 'checklist.new_schedule' : 'checklist.new_audit_schedule');
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
